import Image from 'next/image';
import { useRouter } from 'next/router';
import { isLinkAbsolute, modifyLinks } from '../../util/DataUtil';
import { Constants } from '../../util/Constants';
import {
  getEditorColor,
  brandColor,
  getClassTextForColor,
} from '../../util/ColorUtil';
import { useState, useRef, useEffect } from 'react';
import { cleanHTML } from '../utils/htmlUtils';
import FieldLinkButton from '../base/field-link-button';

const CLOUDINARY_VIDEO_BASE =
  'https://res.cloudinary.com/digitalrealty/video/upload/q_auto,f_auto/';

const stripPTagsForH2 = (html) => {
  if (!html) return html;
  return html
    .replace(/<\/p>\s*<p[^>]*>/gi, '<br>')
    .replace(/<\/?p[^>]*>/gi, '');
};

const ContentImage = ({ data }) => {
  const router = useRouter();
  const { locale } = router;
  const emphasizedTextColor = getEditorColor(
    data?.field_content_image_text[0]?.value
  );
  const [isHoverActive, setIsHoverActive] = useState(false);
  const videoRef = useRef(null);

  const variant = data?.field_content_image_variant?.[0]?.value;
  const isAnimated = variant === 'animated';

  useEffect(() => {
    if (!isAnimated || !videoRef.current) return;

    const video = videoRef.current;

    video.preload = 'auto';
    video.load();

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (video.readyState >= 3) {
            video.play();
          } else {
            video.addEventListener('canplay', () => video.play(), {
              once: true,
            });
          }
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [isAnimated]);

  const correctInternalLink = (link) => {
    if (!link) return;
    if (link.includes('internal')) {
      return link.substring(9);
    }
    return link;
  };

  if (isAnimated) {
    const bgColorValue = data?.field_background_color?.[0]?.value;
    const videoId = data?.field_content_image_image?.webimage || null;
    const videoType = data?.field_content_image_image?.type == 'video';
    const imageId =
      videoType && videoId
        ? null
        : data?.field_content_image_image?.[0] || null;
    const ctaData = data?.field_link?.[0];

    const themeClass = getClassTextForColor(bgColorValue) || 'dark';

    return (
      <section
        className={`content-image animated ${themeClass}`}
        style={{ backgroundColor: brandColor(bgColorValue) || '#000000' }}
      >
        <div className="inner-container">
          <div className="text-content">
            {data?.field_intro_text?.[0]?.value && (
              <span
                className="intro-text"
                dangerouslySetInnerHTML={{
                  __html: cleanHTML(
                    modifyLinks(data.field_intro_text[0].value, locale)
                  ),
                }}
              />
            )}
            {data?.field_content_image_text?.[0]?.value && (
              <h2
                className="heading"
                dangerouslySetInnerHTML={{
                  __html: cleanHTML(
                    stripPTagsForH2(
                      modifyLinks(
                        data.field_content_image_text[0].value,
                        locale
                      )
                    )
                  ),
                }}
              />
            )}
            {data?.field_description?.[0]?.value && (
              <span
                className="description"
                dangerouslySetInnerHTML={{
                  __html: cleanHTML(
                    modifyLinks(data.field_description[0].value, locale)
                  ),
                }}
              />
            )}
            {ctaData && (
              <FieldLinkButton
                link={ctaData}
                blockTypeClass={'content-image'}
                bgColor={themeClass === 'dark' ? 'black' : ''}
                pathname={true}
              />
            )}
          </div>
        </div>
        {(videoId || imageId) && (
          <div className="media-content">
            {videoType && videoId ? (
              <video
                ref={videoRef}
                muted
                playsInline
                preload="auto"
                className="campaign-video"
              >
                <source
                  src={`${CLOUDINARY_VIDEO_BASE}${videoId}.mp4`}
                  type="video/mp4"
                />
              </video>
            ) : (
              <Image
                src={imageId}
                alt=""
                className="campaign-image"
                layout="fill"
                objectFit="cover"
                objectPosition="center"
                priority
              />
            )}
          </div>
        )}
      </section>
    );
  }

  return (
    <div className={'content-image black div-content-image-link'}>
      <div
        onMouseEnter={() => setIsHoverActive(true)}
        onMouseLeave={() => setIsHoverActive(false)}
        className={`div-content-image-link left${isHoverActive ? ' hovered' : ''}`}
        onClick={() => {
          let link = correctInternalLink(data.field_link[0]?.uri);

          if (isLinkAbsolute(link)) {
            window.open(link, '_blank');
          } else {
            router.push(link);
          }
        }}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: cleanHTML(
              modifyLinks(data?.field_content_image_text[0]?.value, locale)
            ),
          }}
          className={`title ${emphasizedTextColor ? emphasizedTextColor : ''}`}
        />
        {data?.field_link[0]?.title ? (
          <FieldLinkButton
            bgColor={'black'}
            blockTypeClass={'content-image'}
            hasCtaArrow={true}
            link={data.field_link[0]}
            pathname={true}
          />
        ) : (
          <></>
        )}
      </div>
      <div
        className={'right'}
        style={{
          backgroundImage:
            'url(' +
            Constants.cloudinaryImage +
            data?.field_content_image_image[0] +
            ')',
        }}
      />
    </div>
  );
};

export default ContentImage;
